/* Copyright (c) 2017 Backpack Health LLC */

import React from 'react';

import { Allergy, Condition, GeneVariantResult, Lab, Measurement, Medication, OtherTreatment, Procedure, Vaccination } from './HealthEntryOccurrenceTemplates';

import { ScrollAwareScroller } from '../../components/ScrollAware';
import ContentFrame from '../../applicationFrame/ContentFrame';
import AppNavHealthEntryScrollable from '../../appAuthorized/presenters/AppNavHealthEntryScrollable';
import HealthEntryFooter from './components/HealthEntryFooter';
import { HECategoryCardTitle } from './components/CardTitle';
import { ContentWrapperNoTopMargin } from '../../components/ContentWrapper';
import ContentLoader from '../../components/ContentLoader';

import HealthEntryModel from '../../../models/HealthEntry';
import RouteNames from '../../appAuthorized/RouteNames';
import HealthEntryCategoryNames from '../../../constants/HealthEntryCategoryNames';

import { useHealthEntryForActiveProfile, useHealthEntryAndValueForActiveProfile } from '../../hooks/useHealthEntry';
import { useNotesForActiveProfile } from '../../hooks/useNote';
import { useFilesForActiveProfile } from '../../hooks/useFile';
import { useActiveUser } from '../../hooks/useUser';
import { useActiveProfile } from '../../hooks/useProfile';
import { useNavigate } from '../../hooks/useNavigate';
import { useMedliConstantsStoppingReasons, useMedliUnits } from '../../hooks/useMedli';
import { useShareCardDefinitionListForActiveProfile, useShareCardListForActiveProfile } from '../../hooks/useShareCard';
import { useLists } from '../../hooks/useLists';
import { useHealthEntryTrackableListForActiveProfile } from '../../hooks/useTrackable';
import { useUIState } from '../../hooks/useUIState';

const useRequiredData = (healthEntry) => {
  const [scDefData] = useShareCardDefinitionListForActiveProfile();
  const [scData] = useShareCardListForActiveProfile();
  const [listData] = useLists();
  const [medliData] = useMedliUnits();
  const [trackableData] = useHealthEntryTrackableListForActiveProfile(healthEntry.id);

  return scDefData.isLoaded && scData.isLoaded && listData.isLoaded && medliData.isLoaded && trackableData.isLoaded;
};

function getComponent(category) {
  switch (category) {
    case HealthEntryCategoryNames.lab:
      return Lab;
    case HealthEntryCategoryNames.measurement:
      return Measurement;
    case HealthEntryCategoryNames.geneticResult:
      return GeneVariantResult;
    case HealthEntryCategoryNames.condition:
      return Condition;
    case HealthEntryCategoryNames.procedure:
      return Procedure;
    case HealthEntryCategoryNames.medication:
      return Medication;
    case HealthEntryCategoryNames.otherTreatment:
      return OtherTreatment;
    case HealthEntryCategoryNames.allergy:
      return Allergy;
    case HealthEntryCategoryNames.vaccination:
      return Vaccination;
    default:
      return null;
  }
}
interface Props {
  healthEntryId: number;
  healthEntryValueId?: number;
  isHeaderDefaultExpanded: boolean;
  isNew: boolean;
}
const BaseOccurrence = (props) => {
  const {
    isHeaderDefaultExpanded,
    category,
    subCategoryName,
    heInstance,
    onDelete,
    onToggleFlag,
    onToggleIsCurrent,
    healthEntryData,
    healthEntryValueData,
    isNew,
  } = props;

  const [{ preferredLanguageCode }] = useActiveUser();
  const [{ profileId }] = useActiveProfile();
  const reqLoaded = useRequiredData(healthEntryData);
  const isLoaded = reqLoaded && props.isLoaded;

  const hasCategory = Boolean(category) && category !== 'missing';
  const HealthEntryComponent = getComponent(category);
  const [{ isLoading }] = useUIState();

  return (
    <ContentLoader isContentLoaded={isLoaded && !isLoading} blockRenderUntilLoaded={!isLoaded}>

    <ContentFrame className="bkgd-color-light-neutral">
      <ScrollAwareScroller>
        <AppNavHealthEntryScrollable
          deletePhraseId="heSummary.deleteConfirm.button.label"
          isDefaultExpanded={isHeaderDefaultExpanded}
        />

        <ContentWrapperNoTopMargin>
          <HECategoryCardTitle category={category} type={subCategoryName} />

          {
            hasCategory ? (
              <HealthEntryComponent
                healthEntry={heInstance}
                parentHealthEntryId={healthEntryValueData ? healthEntryData.id : null}
                profileId={profileId}
                languageCode={preferredLanguageCode}
                isNew={isNew}
              />
            ) : null
          }

          <HealthEntryFooter
            onDelete={onDelete}
            onToggleFlag={onToggleFlag}
            onToggleIsCurrent={onToggleIsCurrent}
            healthEntry={healthEntryData}
            healthEntryValue={healthEntryValueData}
          />

        </ContentWrapperNoTopMargin>
      </ScrollAwareScroller>
    </ContentFrame>
    </ContentLoader>
  );
};

const ParentHealthEntryOccurrence = (props) => {
  const { healthEntryId, isHeaderDefaultExpanded, isNew } = props;

  const [{ healthEntry, category, isLoaded }, heActions] = useHealthEntryForActiveProfile(healthEntryId);
  useFilesForActiveProfile();
  useNotesForActiveProfile();
  const [, navActions] = useNavigate();
  const [, stopReasonActions] = useMedliConstantsStoppingReasons();

  const subCategoryName = HealthEntryModel.subCategoryName(healthEntry);

  return (
    <BaseOccurrence
      isHeaderDefaultExpanded={isHeaderDefaultExpanded}
      category={category}
      isLoaded={isLoaded}
      subCategoryName={subCategoryName}
      heInstance={heActions.instantiateHealthEntry()}
      healthEntryData={healthEntry}
      healthEntryValueData={null}
      isNew={isNew}
      onDelete={() => {
        heActions.apiDeleteHealthEntry().then(() => {
          navActions.goBack(RouteNames.myBackpack());
        });
      }}
      onToggleFlag={() => {
        heActions.apiToggleFlagged();
      }}
      onToggleIsCurrent={() => {
        const isStoppingReasonReaction = stopReasonActions.isReaction(healthEntry.stopping_reason);
        const newData = HealthEntryModel.toggleIsCurrent(healthEntry, { isStoppingReasonReaction });

        heActions.apiUpdate(newData);
      }}
    />
  );
};

const ChildHealthEntryOccurrence = (props) => {
  const { healthEntryId, healthEntryValueId, isHeaderDefaultExpanded, isNew } = props;
  const [heValueQuery, heValueActions] = useHealthEntryAndValueForActiveProfile(healthEntryId, healthEntryValueId);
  useMedliConstantsStoppingReasons();
  useFilesForActiveProfile();
  useNotesForActiveProfile();
  const [, navActions] = useNavigate();

  const parentHeData = heValueQuery.parentHealthEntry;
  const heValueData = heValueQuery.healthEntryValue;
  const category = heValueQuery.category;
  const isLoaded = heValueQuery.isLoaded;

  return (
    <BaseOccurrence
      isHeaderDefaultExpanded={isHeaderDefaultExpanded}
      category={category}
      isLoaded={isLoaded}
      subCategoryName=""
      isNew={isNew}
      heInstance={heValueActions.instantiateHealthEntryValue()}
      healthEntryData={parentHeData}
      healthEntryValueData={heValueData}
      onDelete={() => {
        heValueActions.apiDeleteValue().then(() => {
          navActions.goBack(RouteNames.healthEntry(healthEntryId));
        });
      }}
      onToggleFlag={() => {
        heValueActions.apiToggleFlagged();
      }}
      onToggleIsCurrent={null}
    />
  );
};

const SingleOccurrence: React.FC<Props> = ({ healthEntryValueId, ...props }) => {
  return healthEntryValueId
    ? <ChildHealthEntryOccurrence healthEntryValueId={healthEntryValueId} {...props} />
    : <ParentHealthEntryOccurrence {...props} />;
};

export default SingleOccurrence;
